import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, ChevronDown, ShieldAlert, Target, ShieldCheck,
  Eye, Lightbulb, HelpCircle, FileCode2, History, X, CheckSquare, Clock, CalendarClock, Gauge, FlaskConical,
} from 'lucide-react';

const PRIORITY_STYLE = {
  critical: 'text-rose-300 bg-rose-500/15 border-rose-500/30',
  high: 'text-amber-300 bg-amber-500/15 border-amber-500/30',
  normal: 'text-sky-300 bg-sky-500/15 border-sky-500/30',
  low: 'text-slate-300 bg-slate-500/15 border-slate-500/30',
};
const CONFIDENCE_STYLE = {
  high: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  moderate: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  low: 'text-slate-300 bg-slate-500/10 border-slate-500/30',
};

// Primary action label + the "what happens next" copy shown before confirming.
const ACTION_META = {
  validate_assign: { label: 'Validate and assign', next: 'This validates the recommendation and moves it to In progress. No approval is required.', primary: true },
  submit_for_approval: { label: 'Submit for approval', next: 'This routes the request for independent approval. You will not be able to approve it yourself.', primary: true },
  approve_assign: { label: 'Approve and assign', next: 'This records your independent approval so the action can be assigned and completed.', primary: true },
  return_for_info: { label: 'Return for more information', next: 'This sends the request back to the submitter with your note.', reason: true },
  mark_completed: { label: 'Mark as completed', next: 'This records the action as complete. The result will be measured when data is available.', primary: true },
  withdraw: { label: 'Withdraw request', next: 'This withdraws your pending request.' },
  investigate: { label: 'Investigate first', next: 'This moves the item to In progress while you confirm the driver before acting.' },
  defer: { label: 'Defer', next: 'This defers the item. It stays on record and can be revived later.' },
  dismiss: { label: 'Dismiss', next: 'This dismisses the recommendation. A reason is required and kept in the activity history.', reason: true },
};

const STATEMENT_STYLE = {
  observed_fact: { label: 'Observed fact', cls: 'text-sky-300' },
  calculated_estimate: { label: 'Calculated estimate', cls: 'text-amber-300' },
  system_recommendation: { label: 'System recommendation', cls: 'text-primary' },
  human_decision: { label: 'Human decision', cls: 'text-emerald-300' },
};

function Section({ n, title, children, testId }) {
  return (
    <div className="py-4 border-b border-border/40 last:border-0" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center">{n}</span>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      </div>
      <div className="text-[15px] text-foreground leading-relaxed pl-7">{children}</div>
    </div>
  );
}

function ActionModal({ action, forceReason, guardrailNote, onClose, onConfirm }) {
  const meta = ACTION_META[action] || {};
  const [reason, setReason] = useState('');
  const [assignee, setAssignee] = useState('');
  const [checkBack, setCheckBack] = useState('');
  const [criterion, setCriterion] = useState('');
  const [busy, setBusy] = useState(false);
  const needReason = !!meta.reason || !!forceReason;
  const isAssign = action === 'validate_assign' || action === 'approve_assign';
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-testid="action-modal">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-background shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border/40">
          <h4 className="text-base font-semibold text-foreground">{meta.label}</h4>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-muted-foreground" data-testid="action-next-copy">{meta.next}</p>
          {guardrailNote && (
            <p className="text-[13px] text-amber-300 bg-amber-500/10 rounded-lg p-2.5" data-testid="guardrail-note">{guardrailNote}</p>
          )}
          {isAssign && (
            <div className="space-y-2 rounded-lg bg-muted/30 p-3" data-testid="assign-fields">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Assign an action (optional)</p>
              <input data-testid="assign-assignee" value={assignee} onChange={e => setAssignee(e.target.value)} placeholder="Responsible person" className="w-full h-9 px-3 rounded-lg bg-background/60 border border-border/40 text-sm text-foreground outline-none" />
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-muted-foreground w-24">Check back</label>
                <input data-testid="assign-checkback" type="date" value={checkBack} onChange={e => setCheckBack(e.target.value)} className="flex-1 h-9 px-3 rounded-lg bg-background/60 border border-border/40 text-sm text-foreground outline-none" />
              </div>
              <textarea data-testid="assign-criterion" value={criterion} onChange={e => setCriterion(e.target.value)} rows={2} placeholder="Done when… (completion criterion)" className="w-full px-3 py-2 rounded-lg bg-background/60 border border-border/40 text-sm text-foreground outline-none resize-none" />
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {needReason ? 'Reason (required)' : 'Note (optional)'}
            </label>
            <textarea
              data-testid="action-reason-input"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground outline-none focus:border-primary/40 resize-none"
              placeholder={needReason ? 'Explain why…' : 'Add context…'}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-border/40">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/50">Cancel</button>
          <button
            data-testid="action-confirm-btn"
            disabled={busy || (needReason && !reason.trim())}
            onClick={async () => {
              setBusy(true);
              try {
                const extra = isAssign ? { assignee_name: assignee.trim(), check_back_date: checkBack, completion_criterion: criterion.trim() } : {};
                await onConfirm(action, reason.trim(), extra);
              } finally { setBusy(false); }
            }}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? 'Working…' : `Confirm — ${meta.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function EvidenceGroup({ icon: Icon, title, children, defaultOpen, testId }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="rounded-xl border border-border/40 bg-card/30 overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground"><Icon className="w-4 h-4 text-muted-foreground" />{title}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-4 pb-4 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

export default function RecommendationDetailPage() {
  const { recId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAction, setModalAction] = useState(null);
  const [prioOpen, setPrioOpen] = useState(false);
  const [prioLevel, setPrioLevel] = useState('high');
  const [prioReason, setPrioReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, act] = await Promise.all([
        api.getWorkRecommendation(recId),
        api.getWorkActivity(recId),
      ]);
      setD(detail);
      setActivity(act.events || []);
    } catch (e) {
      toast.error(e.message || 'Could not load this recommendation');
    } finally {
      setLoading(false);
    }
  }, [recId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action, reason, extra) => {
    try {
      const res = await api.actOnWork(recId, action, reason, extra);
      toast.success(res.next || 'Done');
      setModalAction(null);
      await load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    }
  };

  const updateAction = async (actionId, body) => {
    try {
      const res = await api.updateAction(actionId, body);
      toast.success(res.measure_copy || 'Action updated');
      await load();
    } catch (e) {
      toast.error(e.message || 'Could not update action');
    }
  };

  const doOverride = async () => {
    if (!prioReason.trim()) { toast.error('A reason is required'); return; }
    try {
      await api.overrideWorkPriority(recId, prioLevel, prioReason.trim());
      toast.success('Priority overridden');
      setPrioOpen(false); setPrioReason('');
      await load();
    } catch (e) {
      toast.error(e.message || 'Override failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (!d) return null;

  const decision = d.decision || {};
  const ev = d.evidence || {};
  const primaryActions = decision.primary_actions || [];
  const secondaryActions = decision.secondary_actions || [];

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="recommendation-detail-page">
      <button onClick={() => navigate('/my-work')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5" data-testid="detail-back">
        <ArrowLeft className="w-4 h-4" /> My Work
      </button>

      <h1 className="text-2xl font-bold text-foreground leading-tight" data-testid="detail-title">{d.title}</h1>

      {/* Priority + Confidence — always separate, each with a reason */}
      <div className="flex flex-wrap gap-3 mt-4">
        <div className={`flex-1 min-w-[240px] rounded-xl border p-3 ${PRIORITY_STYLE[d.priority?.level] || PRIORITY_STYLE.normal}`} data-testid="detail-priority">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1">
              {d.priority?.level === 'critical' && <ShieldAlert className="w-3.5 h-3.5" />}
              {d.priority?.level} priority
            </span>
            <button onClick={() => setPrioOpen(o => !o)} className="text-[11px] underline opacity-80 hover:opacity-100" data-testid="priority-override-toggle">Override</button>
          </div>
          <p className="text-[13px] mt-1 opacity-90">{d.priority?.reason}</p>
          {d.priority?.overridden && <p className="text-[11px] mt-1 opacity-70">Original: {d.priority?.assessed_level} — {d.priority?.assessed_reason}</p>}
          {prioOpen && (
            <div className="mt-2 space-y-2" data-testid="priority-override-form">
              <select value={prioLevel} onChange={e => setPrioLevel(e.target.value)} className="w-full h-8 px-2 rounded-lg bg-background/60 border border-border/40 text-xs text-foreground">
                {['critical', 'high', 'normal', 'low'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <input value={prioReason} onChange={e => setPrioReason(e.target.value)} placeholder="Reason (required)" data-testid="priority-override-reason" className="w-full h-8 px-2 rounded-lg bg-background/60 border border-border/40 text-xs text-foreground outline-none" />
              <button onClick={doOverride} data-testid="priority-override-save" className="w-full h-8 rounded-lg bg-primary text-primary-foreground text-xs font-medium">Save override</button>
            </div>
          )}
        </div>
        <div className={`flex-1 min-w-[240px] rounded-xl border p-3 ${CONFIDENCE_STYLE[d.confidence?.level] || CONFIDENCE_STYLE.moderate}`} data-testid="detail-confidence">
          <span className="text-xs font-bold uppercase tracking-wider">{d.confidence?.level} confidence</span>
          <p className="text-[13px] mt-1 opacity-90">{d.confidence?.reason}</p>
        </div>
        <div className="flex items-center px-3 rounded-xl border border-border/40 bg-muted/30 text-sm text-muted-foreground" data-testid="detail-area">{d.area}</div>
      </div>

      {/* Guided 7 questions */}
      <div className="mt-6 rounded-2xl border border-border/50 bg-card/40 backdrop-blur px-5">
        <Section n={1} title="What was found?" testId="q-what-found">{d.what_found}</Section>
        <Section n={2} title="Why does it matter?" testId="q-why-matters">{d.why_matters}</Section>
        <Section n={3} title="Recommended next step" testId="q-recommended">{d.recommended_step}</Section>
        <Section n={4} title="How confident is the evidence?" testId="q-confidence">
          <span className="capitalize">{d.confidence?.level} confidence.</span> {d.confidence?.reason}
        </Section>
        <Section n={5} title="If no action is taken" testId="q-no-action">{d.no_action}</Section>
        <Section n={6} title="Your role" testId="q-your-role">
          {d.your_role}
          {decision.next_actor && <span className="block text-[13px] text-muted-foreground mt-1">Expected to act next: <strong>{decision.next_actor}</strong>.</span>}
        </Section>
        <Section n={7} title="What you can do next" testId="q-actions">
          <div className="flex flex-wrap gap-2">
            {primaryActions.length === 0 && <span className="text-sm text-muted-foreground">No action needed from you right now.</span>}
            {primaryActions.map(a => (
              <button key={a} onClick={() => setModalAction(a)} data-testid={`primary-action-${a}`}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90">
                {(ACTION_META[a] || {}).label || a}
              </button>
            ))}
            {secondaryActions.map(a => (
              <button key={a} onClick={() => setModalAction(a)} data-testid={`secondary-action-${a}`}
                className="px-3 py-2 rounded-lg text-sm text-muted-foreground border border-border/50 hover:text-foreground hover:bg-muted/40">
                {(ACTION_META[a] || {}).label || a}
              </button>
            ))}
            <button onClick={() => {
              const q = new URLSearchParams();
              if (d.model_id) q.set('model_id', d.model_id);
              if (d.business_area) q.set('scope_lob', d.business_area);
              const cat = (d.category || '').toLowerCase();
              const lever = cat.includes('pricing') ? ['rate_change_pct', '-5']
                : cat.includes('reserv') ? ['reserve_strengthen_pct', '10']
                : cat.includes('claim') || cat.includes('frequency') ? ['frequency_change_pct', '-5']
                : ['loss_trend_pct', '-5'];
              q.set(lever[0], lever[1]);
              navigate(`/admin/what-if-analysis?${q.toString()}`);
            }} data-testid="launch-whatif"
              className="px-3 py-2 rounded-lg text-sm text-violet-600 dark:text-violet-300 border border-violet-500/40 hover:bg-violet-500/10 inline-flex items-center gap-1.5">
              <FlaskConical className="w-4 h-4" /> Run What-if analysis
            </button>
          </div>
        </Section>
      </div>

      {/* Goals + Guardrails inline */}
      {(d.goals?.length > 0 || d.guardrails?.length > 0) && (
        <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="detail-goals-guardrails">
          {d.goals?.length > 0 && (
            <div className="rounded-xl border border-border/40 bg-card/30 p-4" data-testid="detail-goals">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2"><Target className="w-4 h-4 text-primary" /> Related goals</div>
              {d.goals.map(g => (
                <div key={g.objective_id} className="mb-2 last:mb-0" data-testid={`goal-${g.objective_id}`}>
                  <p className="text-[13px] text-foreground">{g.name}</p>
                  {(g.baseline || g.target_value) && (
                    <p className="text-[12px] text-muted-foreground">
                      {g.metric ? `${g.metric}: ` : ''}{g.baseline || '—'} → {g.target_value || g.target_label || '—'}
                      {g.target_period ? ` by ${g.target_period}` : ''}
                      {g.business_owner ? ` · ${g.business_owner}` : ''}
                    </p>
                  )}
                  {g.progress && g.progress.percent != null && (
                    <div className="mt-1" data-testid={`goal-progress-${g.objective_id}`}>
                      <div className="flex items-center justify-between text-[10px] mb-0.5">
                        <span className="text-muted-foreground">now {g.progress.current}</span>
                        <span className={`font-semibold ${g.progress.on_track ? 'text-emerald-300' : g.progress.status === 'off_track' ? 'text-rose-300' : 'text-amber-300'}`}>
                          {g.progress.status === 'achieved' ? 'Achieved' : g.progress.on_track ? 'On track' : g.progress.status === 'off_track' ? 'Off track' : 'At risk'} · {g.progress.percent}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted/50 overflow-hidden">
                        <div className={`h-full ${g.progress.on_track ? 'bg-emerald-400' : g.progress.status === 'off_track' ? 'bg-rose-400' : 'bg-amber-400'}`} style={{ width: `${g.progress.percent}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {d.guardrails?.length > 0 && (
            <div className={`rounded-xl border p-4 ${d.guardrails.some(g => g.level === 'required_control') ? 'border-rose-500/40 bg-rose-500/5' : 'border-amber-500/30 bg-amber-500/5'}`} data-testid="detail-guardrails">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-300 mb-2"><ShieldCheck className="w-4 h-4" /> Guardrails</div>
              {d.guardrails.map((g, i) => (
                <div key={g.objective_id || i} className="mb-2 last:mb-0" data-testid={`guardrail-${g.objective_id || i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-amber-100">{g.name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${g.level === 'required_control' ? 'bg-rose-500/20 text-rose-200' : 'bg-amber-500/20 text-amber-200'}`}>
                      {g.level === 'required_control' ? 'Required control' : 'Warning'}
                    </span>
                  </div>
                  {g.threshold && <p className="text-[12px] text-amber-200/80">Threshold: {g.threshold}</p>}
                  {(g.response || g.note) && <p className="text-[12px] text-amber-200/70">{g.response || g.note}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Business Timeline — context for interpreting the result (never asserted as cause) */}
      {(d.business_timeline?.length > 0) && (
        <div className="mt-5 rounded-xl border border-border/40 bg-card/30 p-4" data-testid="detail-business-timeline">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2"><CalendarClock className="w-4 h-4 text-muted-foreground" /> Business events that may have influenced results</div>
          <p className="text-[11px] text-muted-foreground/70 mb-2">These events touch this area. They may have contributed to a result — they are not assumed to have caused it.</p>
          {d.business_timeline.map(e => (
            <div key={e.event_id} className="flex items-center justify-between gap-2 text-[12px] py-1 border-t border-border/20 first:border-0" data-testid={`bt-event-${e.event_id}`}>
              <span className="text-foreground">{e.name}</span>
              <span className="text-muted-foreground whitespace-nowrap">{e.expected_direction} · {e.event_date}</span>
            </div>
          ))}
        </div>
      )}

      {/* Measured result (Phase 4) */}
      {d.result && (
        <div className="mt-5 rounded-xl border border-border/40 bg-card/30 p-4" data-testid="detail-result">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground"><Gauge className="w-4 h-4 text-primary" /> Measured result</div>
            <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-muted/60 text-foreground">{d.result.state_label}</span>
          </div>
          <p className="text-[13px] text-muted-foreground mt-1.5">{d.result.interpretation}</p>
          {(d.result.exception_reasons || []).length > 0 && (
            <ul className="mt-1 text-[12px] text-amber-200/80 list-disc pl-4">{d.result.exception_reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>
          )}
          <p className="text-[11px] text-muted-foreground/70 mt-1">Completing the work does not prove it worked — this is a measured estimate.</p>
        </div>
      )}

      {/* Assigned actions (Phase 2) */}
      {(d.actions?.length > 0) && (
        <div className="mt-6" data-testid="detail-actions">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2"><CheckSquare className="w-4 h-4" /> Assigned work</h2>
          <div className="space-y-2">
            {d.actions.map(a => (
              <div key={a.action_id} className="rounded-xl border border-border/40 bg-card/30 p-4" data-testid={`action-${a.action_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{a.title}</p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      Assigned to {(a.assignee || {}).name || 'Unassigned'}
                      {a.check_back_date && <span className="inline-flex items-center gap-1 ml-2"><Clock className="w-3 h-3" /> Check back {a.check_back_date}</span>}
                    </p>
                    {a.completion_criterion && <p className="text-[12px] text-muted-foreground/80 mt-1">Done when: {a.completion_criterion}</p>}
                    {a.result_measure_at && <p className="text-[12px] text-emerald-300/90 mt-1">Result will be measured around {a.result_measure_at}.</p>}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground uppercase tracking-wider whitespace-nowrap">{a.state}</span>
                </div>
                {a.state !== 'done' && a.state !== 'cancelled' && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={() => updateAction(a.action_id, { state: 'done' })} data-testid={`action-done-${a.action_id}`} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90">Mark completed</button>
                    <button onClick={() => { const r = window.prompt('Reason this is blocked?'); if (r) updateAction(a.action_id, { state: 'blocked', blocked_reason: r }); }} data-testid={`action-blocked-${a.action_id}`} className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground border border-border/50 hover:bg-muted/40">Blocked</button>
                    <button onClick={() => { const r = window.prompt('Reason to cancel?'); if (r) updateAction(a.action_id, { state: 'cancelled', reason: r }); }} data-testid={`action-cancel-${a.action_id}`} className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground border border-border/50 hover:bg-muted/40">Cancel</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progressive evidence disclosure */}
      <div className="mt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Supporting evidence</h2>
        <div className="space-y-2">
          <EvidenceGroup icon={Eye} title="What we observed" defaultOpen testId="evidence-observed">
            {(ev.observed || []).length ? <ul className="space-y-1 list-disc pl-4">{ev.observed.map((o, i) => <li key={i}>{o}</li>)}</ul> : 'No governed observations recorded.'}
          </EvidenceGroup>
          <EvidenceGroup icon={Lightbulb} title="Why we reached this conclusion" testId="evidence-conclusions">
            {(ev.conclusions || []).length ? <ul className="space-y-1 list-disc pl-4">{ev.conclusions.map((o, i) => <li key={i}>{o}</li>)}</ul> : '—'}
          </EvidenceGroup>
          <EvidenceGroup icon={HelpCircle} title="What remains uncertain" testId="evidence-uncertain">
            {(ev.uncertainties || []).length ? <ul className="space-y-1 list-disc pl-4">{ev.uncertainties.map((o, i) => <li key={i}>{o}</li>)}</ul> : 'No material gaps noted.'}
          </EvidenceGroup>
          <EvidenceGroup icon={FileCode2} title="Technical record" testId="evidence-technical">
            <pre className="text-[11px] whitespace-pre-wrap break-words text-muted-foreground/80 bg-background/50 rounded-lg p-3 overflow-auto max-h-64">{JSON.stringify(ev.technical || {}, null, 2)}</pre>
          </EvidenceGroup>
        </div>
      </div>

      {/* Activity history */}
      <div className="mt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2"><History className="w-4 h-4" /> Activity history</h2>
        <div className="rounded-2xl border border-border/50 bg-card/30 divide-y divide-border/30" data-testid="activity-timeline">
          {activity.length === 0 && <p className="p-4 text-sm text-muted-foreground">No activity yet.</p>}
          {activity.map((e, i) => {
            const st = STATEMENT_STYLE[e.statement_type] || STATEMENT_STYLE.human_decision;
            return (
              <div key={i} className="p-4" data-testid={`activity-event-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{e.label}</span>
                  <span className="text-[10px] text-muted-foreground">{e.at ? new Date(e.at).toLocaleString() : ''}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${st.cls}`}>{st.label}</span>
                  <span className="text-[11px] text-muted-foreground">· {e.actor}</span>
                </div>
                {e.detail && <p className="text-[13px] text-muted-foreground mt-1">{e.detail}</p>}
              </div>
            );
          })}
        </div>
      </div>

      {modalAction && (() => {
        const proceed = ['validate_assign', 'approve_assign', 'submit_for_approval'].includes(modalAction);
        const gWarn = proceed && (decision.guardrail_warning || decision.guardrail_block);
        const gNote = gWarn ? (decision.guardrail_block
          ? 'A required-control guardrail applies. Document the risk before proceeding.'
          : 'A guardrail warning applies. You may proceed, but you must document the risk.') : '';
        return <ActionModal action={modalAction} forceReason={gWarn} guardrailNote={gNote} onClose={() => setModalAction(null)} onConfirm={doAction} />;
      })()}
    </div>
  );
}
